/**
 * iFrame Detection Utilities
 *
 * Provides utilities to detect if the application is running inside an iFrame
 * and apply appropriate styling and behavior adjustments for embedded contexts.
 */

/**
 * Check if the application is running inside an iFrame
 *
 * @returns true if running in iFrame, false otherwise
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch (e) {
    // If we can't access window.top due to cross-origin restrictions,
    // we're definitely in an iframe
    return true
  }
}

/**
 * Get the parent window origin if available
 *
 * @returns parent origin or null if not accessible
 */
export function getParentOrigin(): string | null {
  try {
    if (window.parent && window.parent !== window.self) {
      return window.parent.location.origin
    }
  } catch (e) {
    // Cross-origin restriction - cannot access parent origin
    return null
  }
  return null
}

/**
 * Apply iFrame-specific class to document body
 *
 * This function should be called early in the application lifecycle
 * to apply the 'iframe-mode' class to the body element for CSS targeting.
 */
export function applyIframeMode(): void {
  if (isInIframe()) {
    document.body.classList.add('iframe-mode')

    // Also add to #app element if it exists
    const appElement = document.getElementById('app')
    if (appElement) {
      appElement.classList.add('iframe-mode')
    }
  }
}

/**
 * Remove iFrame-specific class from document body
 */
export function removeIframeMode(): void {
  document.body.classList.remove('iframe-mode')

  const appElement = document.getElementById('app')
  if (appElement) {
    appElement.classList.remove('iframe-mode')
  }
}

/**
 * Get viewport dimensions
 *
 * Useful for adjusting layout based on available space in iFrame
 */
export function getViewportDimensions(): { width: number; height: number } {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

/**
 * Send message to parent window (if in iFrame)
 *
 * @param message - Message to send to parent
 * @param targetOrigin - Target origin (default '*' for any origin)
 */
export function sendMessageToParent(message: any, targetOrigin: string = '*'): void {
  if (isInIframe() && window.parent) {
    try {
      window.parent.postMessage(message, targetOrigin)
    } catch (e) {
      console.error('Failed to send message to parent:', e)
    }
  }
}

/**
 * Listen for messages from parent window
 *
 * @param handler - Callback to handle received messages
 * @returns Function to remove the event listener
 */
export function listenToParentMessages(
  handler: (event: MessageEvent) => void
): () => void {
  window.addEventListener('message', handler)

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handler)
  }
}

/**
 * Notify parent of game events (useful for Learnworlds integration)
 *
 * @param event - Event type (e.g., 'game-started', 'game-completed')
 * @param data - Event data
 */
export function notifyParentGameEvent(event: string, data?: any): void {
  sendMessageToParent({
    type: 'game-event',
    event,
    data,
    timestamp: new Date().toISOString(),
  })
}

/**
 * Request parent window to adjust iFrame height
 *
 * @param height - Desired height in pixels
 */
export function requestHeightAdjustment(height: number): void {
  sendMessageToParent({
    type: 'resize-request',
    height,
  })
}

/**
 * Auto-adjust iFrame height based on content
 *
 * This function observes the document body and sends height adjustment
 * requests to the parent window when content size changes.
 *
 * @returns Function to stop observing
 */
export function enableAutoHeightAdjustment(): () => void {
  if (!isInIframe()) {
    return () => {} // No-op if not in iframe
  }

  const sendHeight = () => {
    const height = document.body.scrollHeight
    requestHeightAdjustment(height)
  }

  // Send initial height
  sendHeight()

  // Observe changes
  const observer = new ResizeObserver(() => {
    sendHeight()
  })

  observer.observe(document.body)

  // Return cleanup function
  return () => {
    observer.disconnect()
  }
}

/**
 * iFrame configuration interface
 */
export interface IframeConfig {
  /** Whether to automatically apply iframe-mode class */
  autoApplyMode: boolean
  /** Whether to automatically adjust height */
  autoAdjustHeight: boolean
  /** Whether to notify parent of game events */
  notifyGameEvents: boolean
}

/**
 * Initialize iFrame utilities with configuration
 *
 * @param config - iFrame configuration options
 * @returns Cleanup function to remove all listeners and observers
 */
export function initializeIframeUtils(config: Partial<IframeConfig> = {}): () => void {
  const fullConfig: IframeConfig = {
    autoApplyMode: true,
    autoAdjustHeight: false,
    notifyGameEvents: true,
    ...config,
  }

  const cleanupFunctions: Array<() => void> = []

  // Apply iframe mode class
  if (fullConfig.autoApplyMode) {
    applyIframeMode()
    cleanupFunctions.push(removeIframeMode)
  }

  // Enable auto height adjustment
  if (fullConfig.autoAdjustHeight && isInIframe()) {
    const stopHeightAdjustment = enableAutoHeightAdjustment()
    cleanupFunctions.push(stopHeightAdjustment)
  }

  // Return cleanup function
  return () => {
    cleanupFunctions.forEach((cleanup) => cleanup())
  }
}
